import { useEffect } from 'react';

import { useOptionalAiAssistant } from './AiAssistantProvider';
import type {
  ProgressNoteContext,
  TreatmentPlanGuidedContext,
  TreatmentPlanRevisionContext,
} from './types';

/**
 * Registers treatment-plan revision context with the shell assistant while mounted.
 * Clears on unmount or when context becomes null.
 */
export function useRegisterTreatmentPlanRevisionContext(
  context: TreatmentPlanRevisionContext | null,
): void {
  const assistant = useOptionalAiAssistant();

  useEffect(() => {
    if (!assistant) return;
    assistant.registerRevisionContext(context);
    return () => {
      assistant.registerRevisionContext(null);
    };
    // Intentionally depend on identity fields, not unstable callback identities when possible.
    // Callers should memoize callbacks.
  }, [
    assistant,
    context?.patientId,
    context?.documentKey,
    context?.disabled,
    context?.geminiAiDisabled,
    context?.readOnly,
    context?.questionnaire.id,
    context,
  ]);
}

/**
 * Registers guided-create eligibility context (without auto-opening).
 */
export function useRegisterTreatmentPlanGuidedContext(
  context: TreatmentPlanGuidedContext | null,
): void {
  const assistant = useOptionalAiAssistant();

  useEffect(() => {
    if (!assistant) return;
    assistant.registerGuidedContext(context);
    return () => {
      assistant.registerGuidedContext(null);
    };
  }, [
    assistant,
    context?.patientId,
    context?.documentKey,
    context?.episodeId,
    context?.questionnaireId,
    context?.geminiAiDisabled,
    context,
  ]);
}

/**
 * Registers progress-note generation context with the shell assistant while mounted.
 * Cleared on unmount, when context becomes null, or when navigating off appointment pages.
 */
export function useRegisterProgressNoteContext(context: ProgressNoteContext | null): void {
  const assistant = useOptionalAiAssistant();

  useEffect(() => {
    if (!assistant) return;
    assistant.registerProgressNoteContext(context);
    return () => {
      assistant.registerProgressNoteContext(null);
    };
  }, [
    assistant,
    context?.appointmentId,
    context?.patientId,
    context?.documentKey,
    context?.geminiAiDisabled,
    context?.canGenerate,
    context?.canRegenerate,
    context,
  ]);
}

export { useAiAssistant, useOptionalAiAssistant } from './AiAssistantProvider';
